import {forwardRef} from 'react';
import {FormProvider} from 'react-hook-form';
import {BrandSection, CreateBrand} from '@tinct/core-api-models';
import {Button, Card, CardContent, CardFooter} from '@tinct/web-components';
import {useCreateOrganizationBrandForm} from '../hooks/use-create-organization-brand-form';
import {createConnectedInputHelpers} from "@tinct/web-form";

/** The brand sections this form edits: its save takes these from the form, the others from the loaded version. */
const VALUE_AND_MESSAGING_SECTIONS: readonly BrandSection[] = ['valueAndMessaging'];

type UpdateValueAndMessagingFormProps = {
  organizationId: string | null;
  onSuccess?: () => void;
  hideFooter?: boolean;
};

export const UpdateValueAndMessagingForm = forwardRef<HTMLFormElement, UpdateValueAndMessagingFormProps>(({
  hideFooter = false,
  organizationId,
  onSuccess,
}, ref) => {
  const { form, isReady, handleSubmit, isLoading } = useCreateOrganizationBrandForm({
    organizationId,
    sections: VALUE_AND_MESSAGING_SECTIONS,
    onSuccess,
  });

  const { ControlledTextareaInput, ControlledTokenInput } = createConnectedInputHelpers<CreateBrand>();

  // The <form> (and so the ref a funnel submits through) only exists once the
  // loaded values are in it: nothing can submit the empty defaults.
  if (!isReady) {
    return <div>Loading...</div>;
  }

  return (
    <FormProvider {...form}>
      <div className="flex flex-col gap-8">
        <form ref={ref} onSubmit={handleSubmit}>
          <Card className="rounded-2xl gap-0 py-0">
            <CardContent className="px-6 pt-2">
              <div className="flex flex-col divide-y">
                <ControlledTextareaInput
                  className="py-6"
                  name="valueAndMessaging.brandMessaging"
                  label="Brand Messaging"
                  direction="horizontal"
                  placeholder="Describe your brand messaging..."
                />
                <ControlledTextareaInput
                  className="py-6"
                  name="valueAndMessaging.keyBenefits"
                  label="Key Benefits"
                  direction="horizontal"
                  placeholder="List key benefits..."
                />
                <ControlledTextareaInput
                  className="py-6"
                  name="valueAndMessaging.painPointsAddressed"
                  label="Pain Points Addressed"
                  direction="horizontal"
                  placeholder="List pain points addressed..."
                />
                <ControlledTextareaInput
                  className="py-6"
                  name="valueAndMessaging.differentiators"
                  label="Differentiators VS Competitors"
                  direction="horizontal"
                  placeholder="Describe what makes you different..."
                />
                <ControlledTextareaInput
                  className="py-6"
                  name="valueAndMessaging.vision"
                  label="Mission & Vision"
                  direction="horizontal"
                  placeholder="Describe your mission and vision..."
                />
                <ControlledTokenInput
                  className="py-6"
                  name="valueAndMessaging.toneOfVoices"
                  label="Tone of Voice"
                  direction="horizontal"
                  placeholder="Add tone..."
                />
                <ControlledTokenInput
                  className="py-6"
                  name="valueAndMessaging.keywords"
                  label="Keywords (SEO)"
                  direction="horizontal"
                  placeholder="Add keyword..."
                />
                <ControlledTextareaInput
                  className="py-6"
                  name="valueAndMessaging.whatToAvoid"
                  label="What to Avoid"
                  direction="horizontal"
                  placeholder="Describe what to avoid..."
                />
                <ControlledTokenInput
                  className="py-6"
                  name="valueAndMessaging.negativeKeywords"
                  label="Negative Keywords"
                  direction="horizontal"
                  placeholder="Add negative keyword..."
                />
              </div>
            </CardContent>
            {!hideFooter && (
              <CardFooter className="border-t justify-end py-6">
                <Button type="submit" isLoading={isLoading}>Save</Button>
              </CardFooter>
            )}
          </Card>
        </form>
      </div>
    </FormProvider>
  );
});

UpdateValueAndMessagingForm.displayName = 'UpdateValueAndMessagingForm';
